import { useTranslation } from 'react-i18next';
import { CloudOff } from 'lucide-react';
import { GUEST_FALLBACK_CURRENCY } from '@/lib/guest';
import { useGuestMenu } from '@/Hooks/useGuestMenu';
import { Button } from '@/components/ui/button';
import { MenuItemCard } from '@/components/MenuItemCard';
import { useAppSelector } from '@/Redux/store';

/**
 * The menu is the first and largest thing a QR guest waits for, so it is served
 * from IndexedDB the instant a copy exists and revalidated behind what is
 * already on screen (`Hooks/useGuestMenu`). Nothing here changes what an order
 * sends: prices are display-only and the server prices what it charges.
 */
export default function Menu() {
  const { t } = useTranslation();
  const session = useAppSelector((s) => s.guest.session);
  const { sections, status, stale, revalidating, refresh } = useGuestMenu();

  const currency = session?.currency ?? GUEST_FALLBACK_CURRENCY;

  // Only a first visit with nothing cached can see this — a returning guest
  // goes straight to content.
  if (status === 'loading') {
    return <p className="py-16 text-center text-sm text-muted-foreground">{t('common.loading')}</p>;
  }

  if (!sections.length) {
    return (
      <div className="space-y-3 py-16 text-center">
        <p className="text-sm text-muted-foreground">
          {status === 'error' ? t('guest.menu.unavailable') : t('guest.menu.empty')}
        </p>
        {status === 'error' && (
          <Button variant="outline" disabled={revalidating} onClick={() => void refresh()}>
            {t('guest.menu.retry')}
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Shown only when the copy on screen could not be confirmed. Saying so
          is what keeps a stale price from reading as a promise. */}
      {stale && (
        <div className="flex items-start gap-2 rounded-lg border border-notice-line bg-notice-bg p-3 text-xs text-notice">
          <CloudOff className="mt-0.5 h-4 w-4 shrink-0" />
          <div className="flex-1 space-y-1">
            <p className="font-medium">{t('guest.menu.savedCopy')}</p>
            <p>{t('guest.menu.savedCopyHint')}</p>
          </div>
          <button
            type="button"
            className="shrink-0 font-medium underline disabled:opacity-50"
            disabled={revalidating}
            onClick={() => void refresh()}
          >
            {t('guest.menu.retry')}
          </button>
        </div>
      )}

      {sections.map((section) => (
        <section key={section.id} className="space-y-3">
          <h2 className="text-lg font-semibold">{section.name}</h2>

          {/* Two columns. One column shows about three items on a phone, so a
              40-item menu is thirteen screens; guests are comparing options,
              not reading one. Stays at two rather than growing on wider
              screens: this is a phone surface held at a table, and a tablet
              showing six across would make each card smaller than its own
              photograph. */}
          <div className="grid grid-cols-2 gap-2">
            {section.items.map((item) => (
              <MenuItemCard key={item.id} item={item} currency={currency} />
            ))}
          </div>
        </section>
      ))}
    </div>
  );
}
